import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import 'trix';
import 'trix/dist/trix.css';
import { patchRichiestaRimborso } from '../services/api_cost_refunds';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';

const FormEditRimborsoCompany = ({ afterSubmitEditRecord, recordToEdit, onCancelEdit }) => {
  const { token } = useContext(AuthContext);
  const [file, setFile] = useState(recordToEdit?.file || null);
  const [formValues, setFormValues] = useState({
    title: recordToEdit?.title || '',
    amount: recordToEdit?.amount || '',
    date: recordToEdit?.date || '',
    description: recordToEdit?.description || '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const descriptionRef = useRef();

  useEffect(() => {
    if (recordToEdit?.description) {
      descriptionRef.current.value = recordToEdit.description;
      document.querySelector("trix-editor").editor.loadHTML(recordToEdit.description);
    }
  }, [recordToEdit]);

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile?.size > 2 * 1024 * 1024) {
      Swal.fire('Errore', 'File troppo grande. Massimo 2MB', 'warning');
      return;
    }
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formValues.title) errors.title = 'Oggetto richiesto';
    if (!formValues.amount || isNaN(formValues.amount) || parseFloat(formValues.amount) <= 0) {
      errors.amount = 'Importo valido richiesto';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeleteFile = () => setFile(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const updatedData = { ...formValues };
    if (recordToEdit?.file !== file) updatedData.file = file;

    setLoading(true);
    try {
      const updatedRecord = await patchRichiestaRimborso(token, recordToEdit.id, updatedData);
      afterSubmitEditRecord(updatedRecord);
      await Swal.fire({ title: 'Salvato!', text: 'Aggiornamento effettuato', icon: 'success', showConfirmButton: false, timer: 1500 });
      onCancelEdit();
    } catch (error) {
      let errorMessage = 'Errore durante l\'aggiornamento.';

      if (error.response?.data) {
        const errorData = error.response.data;
        const messages = [];

        if (typeof errorData === 'string') {
          messages.push(errorData);
        } else if (Array.isArray(errorData)) {
          messages.push(...errorData);
        } else if (typeof errorData === 'object') {
          Object.entries(errorData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              messages.push(`${key}: ${value.join(', ')}`);
            } else if (typeof value === 'string') {
              messages.push(`${key}: ${value}`);
            } else {
              messages.push(`${key}: ${JSON.stringify(value)}`);
            }
          });
        }

        if (messages.length > 0) {
          errorMessage = messages.join('\n');
        }
      }

      await Swal.fire('Errore', errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const css = `
    .trix-button.trix-button--icon.trix-button--icon-attach { display: none; }
    .trix-button-group.trix-button-group--file-tools { display: none; }
  `;

  return (
    <div>
      <style>{css}</style>
      <form onSubmit={handleSubmit} className="shadow-md border border-gray-200 rounded-b-lg px-8 pt-6 pb-8 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Oggetto</label>
            <input name="title" value={formValues.title} onChange={handleFieldChange} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm" />
            {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Data</label>
            <input type="date" name="date" value={formValues.date} onChange={handleFieldChange} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Importo</label>
            <input type="number" step="0.01" name="amount" value={formValues.amount} onChange={handleFieldChange} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm" />
            {formErrors.amount && <p className="text-red-500 text-xs mt-1">{formErrors.amount}</p>}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Allegato</label>
          {recordToEdit?.file && file && recordToEdit.file === file && (
            <div className="mb-2">
              <span className='text-sm text-gray-700 mr-2'>File caricato:</span>
              <a href={recordToEdit.file} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold bg-indigo-500 hover:bg-indigo-400 p-1 text-white rounded-md">Visualizza</a>
              <button type="button" onClick={handleDeleteFile} className="ml-3 text-sm text-red-600 font-semibold underline">Elimina</button>
            </div>
          )}

          {recordToEdit?.file !== file && file && (
            <div className="mb-2">
              <p className="text-sm text-gray-700">Nuovo file selezionato:</p>
              <ul className="text-sm text-gray-600 list-disc pl-5">
                <li><strong>Nome:</strong> {file.name}</li>
                <li><strong>Tipo:</strong> {file.type}</li>
                <li><strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB</li>
              </ul>
              <button type="button" onClick={handleDeleteFile} className="mt-1 text-red-500 text-sm font-semibold underline">Rimuovi</button>
            </div>
          )}

          {!file && (
            <div {...getRootProps({ className: 'border-dashed border-2 border-gray-300 p-4 rounded-md cursor-pointer text-center text-sm text-gray-500' })}>
              <input {...getInputProps()} />
              <p>Trascina qui il file o clicca per selezionarlo (max 2MB)</p>
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note aggiuntive</label>
          <input type="hidden" id="description" name="description" ref={descriptionRef} value={formValues.description} onChange={() => {}} />
          <trix-editor input="description" onChange={(e) => setFormValues(prev => ({ ...prev, description: e.target.innerHTML }))} onBlur={() => setFormValues(prev => ({ ...prev, description: descriptionRef.current.value }))} />
        </div>

        <div className="mt-6 flex justify-center space-x-4">
          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : (
            <>
              <button type="submit" className="bg-indigo-500 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-400 w-24 font-semibold">Aggiorna</button>
              <button type="button" onClick={onCancelEdit} className="text-sm font-semibold ml-3 bg-gray-200 hover:bg-gray-300 w-24 py-2 text-gray-600 rounded-md">Annulla</button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default FormEditRimborsoCompany;